import React, { useState } from 'react';
import { Save, Sun, Moon, Bell, Shield, Building, Phone, Mail, MapPin, Clock } from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import { useTheme } from '../context/ThemeContext';
import './SettingsPage.css';

export default function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const [saved, setSaved]     = useState(false);

  const [profile, setProfile] = useState({
    name: 'AutoElite Service Centre',
    phone: '+91 77194 12031',
    email: 'gurkirat@autoelite.in',
    address: '#24, Kissan Market, Sirhind Road, Patiala, Punjab - 147001',
    gst: '03AABCU9603R1ZV',
    openTime: '08:00', closeTime: '20:00',
    sundayOpen: '10:00', sundayClose: '16:00',
  });

  const [notif, setNotif] = useState({
    bookingAlert: true, serviceComplete: true, lowStock: true,
    dailySummary: false, smsAlerts: true,
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <>
      <TopBar title="Settings" subtitle="Configure your AutoElite admin preferences" />
      <div className="admin-content">
        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>Settings</h1>
            <p>Manage system preferences and business information</p>
          </div>
          <button className="btn btn-primary" onClick={handleSave}>
            <Save size={14} /> {saved ? 'Saved!' : 'Save Changes'}
          </button>
        </div>

        <div className="settings-layout">
          {/* Business Profile */}
          <section className="settings-section card">
            <div className="settings-section__header">
              <Building size={18} />
              <h3>Business Profile</h3>
            </div>
            <div className="settings-grid">
              <div><label className="label">Business Name</label><input className="input" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} /></div>
              <div><label className="label">GST Number</label><input className="input" value={profile.gst} onChange={e => setProfile(p => ({ ...p, gst: e.target.value }))} /></div>
              <div>
                <label className="label"><Phone size={11} /> Phone</label>
                <input className="input" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div>
                <label className="label"><Mail size={11} /> Email</label>
                <input className="input" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label className="label"><MapPin size={11} /> Address</label>
                <textarea className="input" rows={2} style={{ resize: 'vertical' }} value={profile.address} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))} />
              </div>
            </div>
          </section>

          {/* Working Hours */}
          <section className="settings-section card">
            <div className="settings-section__header">
              <Clock size={18} />
              <h3>Working Hours</h3>
            </div>
            <div className="settings-grid">
              <div>
                <label className="label">Mon–Sat Open</label>
                <input className="input" type="time" value={profile.openTime} onChange={e => setProfile(p => ({ ...p, openTime: e.target.value }))} />
              </div>
              <div>
                <label className="label">Mon–Sat Close</label>
                <input className="input" type="time" value={profile.closeTime} onChange={e => setProfile(p => ({ ...p, closeTime: e.target.value }))} />
              </div>
              <div>
                <label className="label">Sunday Open</label>
                <input className="input" type="time" value={profile.sundayOpen} onChange={e => setProfile(p => ({ ...p, sundayOpen: e.target.value }))} />
              </div>
              <div>
                <label className="label">Sunday Close</label>
                <input className="input" type="time" value={profile.sundayClose} onChange={e => setProfile(p => ({ ...p, sundayClose: e.target.value }))} />
              </div>
            </div>
          </section>

          {/* Appearance */}
          <section className="settings-section card">
            <div className="settings-section__header">
              <Sun size={18} />
              <h3>Appearance</h3>
            </div>
            <div className="theme-toggle-row">
              <div>
                <p style={{ fontWeight: 500, fontSize: 14 }}>Theme Mode</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Currently: {theme === 'light' ? 'Light Mode' : 'Dark Mode'}</p>
              </div>
              <button className="theme-toggle-btn" onClick={toggleTheme}>
                {theme === 'light'
                  ? <><Sun size={15} /> Light Mode</>
                  : <><Moon size={15} /> Dark Mode</>
                }
              </button>
            </div>
            <div className="theme-swatches">
              <p className="label" style={{ marginBottom: 12 }}>Current Palette</p>
              {[
                { name: 'Cloud Dancer',  color: '#d08d36' },
                { name: 'Espresso',     color: '#221A18' },
                { name: 'Muted Brass',  color: '#C9A46B' },
                { name: 'Burnt Copper', color: '#A8823E' },
              ].map(({ name, color }) => (
                <div key={name} className="theme-swatch">
                  <div className="theme-swatch__dot" style={{ background: color }} />
                  <span>{name}</span>
                  <code>{color}</code>
                </div>
              ))}
            </div>
          </section>

          {/* Notifications */}
          <section className="settings-section card">
            <div className="settings-section__header">
              <Bell size={18} />
              <h3>Notifications</h3>
            </div>
            <div className="notif-list">
              {[
                { key: 'bookingAlert',    label: 'New Booking Alert',        desc: 'Get notified when a new booking is created' },
                { key: 'serviceComplete', label: 'Service Completion',        desc: 'Alert when a job is marked as ready' },
                { key: 'lowStock',        label: 'Low Inventory Alert',       desc: 'Notify when parts fall below minimum stock' },
                { key: 'dailySummary',   label: 'Daily Summary Email',       desc: 'Receive daily business summary at end of day' },
                { key: 'smsAlerts',      label: 'SMS Customer Alerts',       desc: 'Send SMS to customers on status updates' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="notif-item">
                  <div>
                    <p className="notif-item__label">{label}</p>
                    <p className="notif-item__desc">{desc}</p>
                  </div>
                  <button
                    className={`toggle-btn ${notif[key] ? 'toggle-btn--on' : ''}`}
                    onClick={() => setNotif(n => ({ ...n, [key]: !n[key] }))}
                    aria-label={label}
                  >
                    <span className="toggle-btn__thumb" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Security */}
          <section className="settings-section card">
            <div className="settings-section__header">
              <Shield size={18} />
              <h3>Security</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><label className="label">Current Password</label><input className="input" type="password" placeholder="••••••••" /></div>
              <div><label className="label">New Password</label><input className="input" type="password" placeholder="••••••••" /></div>
              <div><label className="label">Confirm New Password</label><input className="input" type="password" placeholder="••••••••" /></div>
              <button className="btn btn-outline" style={{ alignSelf: 'flex-start' }}>Update Password</button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}